import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectWebsocketConnected } from 'store/conversations/selectors';
import { selectCurrentUser } from 'store/users/selectors';
import { conversationsQueryKey, conversationsQueryOptions } from './useConversations';
import useIsSmallScreen from './useIsSmallScreen';
import useMarkConversationAsRead from './useMarkConversationAsRead';
import useSelectConversations from './useSelectConversations';

/**
 * Loads the conversations list once for the whole app and refreshes it on every
 * WebSocket reconnect (gap-recovery for events emitted while disconnected). Should be
 * mounted *once* at the route shell. Mounting it from individual messaging pages used
 * to multiply requests and races on each navigation.
 *
 * The data lives in TanStack Query under `conversationsQueryKey` and is the single
 * source of truth for the conversations list — read it through `useConversationsList`,
 * `useConversationsByType`, etc. Mutators (websocket handlers, optimistic updates)
 * call `queryClient.setQueryData(conversationsQueryKey, ...)` rather than going through
 * Redux.
 *
 * For marking a conversation as read from inside a page, use `useMarkConversationAsRead`.
 */
export default function useLoadConversations() {
  const isWebsocketConnected = useSelector(selectWebsocketConnected);
  const { conversationId } = useSelectConversations();
  const currentUser = useSelector(selectCurrentUser);
  const { isSmallScreen } = useIsSmallScreen();
  const markConversationAsRead = useMarkConversationAsRead();
  const queryClient = useQueryClient();

  const { data, isSuccess } = useQuery({
    ...conversationsQueryOptions(!!currentUser?.is_caregiver),
    enabled: !!currentUser,
  });

  // On a fresh login on a wide screen (i.e. desktop dashboard layout), automatically
  // open the patient's first conversation and mark it as read so the inbox doesn't
  // start with an unread badge above the conversation that's actually selected.
  useEffect(() => {
    if (!isSuccess || !data || !currentUser || isSmallScreen) return;
    if (currentUser.is_caregiver) return;
    if (conversationId) return;
    if (data.length === 0) return;
    void markConversationAsRead(data[0].id);
  }, [isSuccess, data, currentUser, isSmallScreen, conversationId, markConversationAsRead]);

  // Gap recovery: invalidate (and thus refetch) whenever the WebSocket reconnects.
  useEffect(() => {
    if (isWebsocketConnected) {
      queryClient.invalidateQueries({ queryKey: [...conversationsQueryKey] });
    }
  }, [isWebsocketConnected, queryClient]);
}
